import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { useUserStore } from '@store/user';
import type { MaybeRefOrGetter, Ref } from 'vue';

export type MyOrderAction = App.Data.Responses.Me.MyOrderActionData;

export type MyOrderActionActivity = MyOrderAction & {
    kind: 'order_action';
    activity_at: string | null;
};

export interface MyCompanyDiaryActivity {
    kind: 'company_diary_entry';
    id: number;
    activity_at: string | null;
    title: string;
    status: 'TO_DO' | 'IN_PROGRESS' | 'PAUSED' | 'COMPLETED';
    start_datetime: string | null;
    end_datetime: string | null;
    all_day: boolean;
    type: {
        id: number | null;
        name: string | null;
        colour: string | null;
        icon: string | null;
    };
}

export type MyActivity = MyOrderActionActivity | MyCompanyDiaryActivity;

export function useMyActivity(date: Ref<string>, options: { enabled?: MaybeRefOrGetter<boolean> } = {}) {
    const userStore = useUserStore();
    const enabled = computed(() => toValue(options.enabled ?? true));

    const { data, isLoading, isFetching, refetch, error } = useQuery({
        queryKey: computed(() => ['me/order-actions', date.value, userStore.tenantId]),
        queryFn: async (): Promise<MyActivity[]> => {
            const resp = await window.tenantAxios.get<{ data: MyActivity[] }>('/me/activity', {
                params: { date: date.value },
            });
            return resp.data.data || [];
        },
        enabled,
        retry: false,
        placeholderData: [],
        staleTime: 5000,
    });

    return {
        data,
        isLoading,
        isFetching,
        refetch,
        error,
    };
}

/** Kept for callers outside the Activity modal while they migrate to the mixed feed name. */
export const useMyOrderActions = useMyActivity;

export function useToggleOrderActionCompleted() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: { id: number; completed: boolean }) => {
            const resp = await window.tenantAxios.patch<{ data: { id: number; completed_at: string | null } }>(
                `/me/order-actions/${input.id}`,
                { completed: input.completed },
            );
            return resp.data.data;
        },
        onMutate: async (input) => {
            await queryClient.cancelQueries({ queryKey: ['me/order-actions'] });

            const snapshots = queryClient.getQueriesData<MyActivity[]>({ queryKey: ['me/order-actions'] });
            const optimisticCompletedAt = input.completed ? new Date().toISOString() : null;

            queryClient.setQueriesData<MyActivity[]>({ queryKey: ['me/order-actions'] }, (old) => {
                if (!old) return old;
                return old.map((action) =>
                    action.kind === 'order_action' && action.id === input.id ?
                        { ...action, completed_at: optimisticCompletedAt }
                    :   action,
                );
            });

            return { snapshots };
        },
        onError: (_err, _input, ctx) => {
            ctx?.snapshots.forEach(([key, data]) => queryClient.setQueryData(key, data));
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['me/order-actions'] });
        },
    });
}
